/**
 * Die Zufallsaktionen an einer offenen Buchung: Roulette, Zufallsrichter,
 * Würfelduell, Joker. Jede Aktion ruft den Server, der zieht und persistiert;
 * die Anzeige läuft dem Ergebnis hinterher.
 */
import { useState } from 'react'
import { api, ApiFehler } from '../api/client.js'
import type { LedgerPosten, Schnaps, Teilnehmer } from '../api/typen.js'
import { Amtsknopf, Fehlermeldung } from './Bausteine.js'
import { Gluecksrad, useRadSteht } from './Roulette.js'

interface RouletteAntwort {
  gezogen: Schnaps
  kandidaten: Schnaps[]
  gezogenIndex: number
}

interface ZufallsrichterAntwort {
  richterMemberId: number
}

interface DuellAntwort {
  duellantA: { memberId: number; wurf: number }
  duellantB: { memberId: number; wurf: number }
  verliererMemberId: number
  wuerfe: Array<[number, number]>
}

export function ZufallsAktionen({
  posten,
  teilnehmer,
  eigeneMemberId,
  neuLaden,
}: {
  posten: LedgerPosten
  teilnehmer: Teilnehmer[]
  eigeneMemberId: number | undefined
  neuLaden: () => void
}) {
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [roulette, setRoulette] = useState<RouletteAntwort | null>(null)
  const radSteht = useRadSteht(roulette !== null)
  const [richter, setRichter] = useState<ZufallsrichterAntwort | null>(null)
  const [duell, setDuell] = useState<DuellAntwort | null>(null)
  const [jokerOffen, setJokerOffen] = useState(false)

  const name = (memberId: number): string =>
    teilnehmer.find((t) => t.memberId === memberId)?.name ?? `Mitglied ${memberId}`

  const anwesende = teilnehmer.filter((t) => t.status === 'da')

  async function rufe<T>(pfad: string, body?: unknown): Promise<T | null> {
    setLaeuft(true)
    setFehler(null)
    try {
      return await api.post<T>(`/api/penalties/${posten.id}/${pfad}`, body)
    } catch (f) {
      setFehler(f instanceof ApiFehler ? f.message : 'Die Ziehung ist fehlgeschlagen.')
      return null
    } finally {
      setLaeuft(false)
    }
  }

  // Roulette-Ansicht: das Rad dreht auf das bereits gezogene Ergebnis.
  if (roulette) {
    return (
      <div className="mt-3 border-t border-eiche pt-4 text-center">
        <p className="protokoll">§2 · Schnapsroulette</p>
        <div className="mt-3">
          <Gluecksrad kandidaten={roulette.kandidaten} gezogenIndex={roulette.gezogenIndex} />
        </div>
        {radSteht && (
          <>
            <p className="mt-3 text-lampe">
              {roulette.gezogen.emoji} {roulette.gezogen.name}
            </p>
            <p className="protokoll mt-1 normal-case">Das Rad hat entschieden. Wohl bekomms.</p>
            <div className="mt-3">
              <Amtsknopf
                onClick={() => {
                  // Ohne das Zuruecksetzen bleibt die Ansicht auf dem Rad
                  // stehen — der Knopf reagiert, aber nichts geschieht.
                  setRoulette(null)
                  neuLaden()
                }}
              >
                Zur Kenntnis genommen
              </Amtsknopf>
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <div className="mt-3 flex flex-col gap-2 border-t border-eiche pt-3">
      {richter && (
        <p className="border border-messing/40 bg-messing/10 px-3 py-2 text-sm text-akte">
          Das Los bestimmt: <span className="text-lampe">{name(richter.richterMemberId)}</span>{' '}
          wählt die Sorte (§2).
        </p>
      )}
      {duell && (
        <p className="border border-messing/40 bg-messing/10 px-3 py-2 text-sm text-akte">
          Würfelduell: {name(duell.duellantA.memberId)} wirft {duell.duellantA.wurf},{' '}
          {name(duell.duellantB.memberId)} wirft {duell.duellantB.wurf} —{' '}
          <span className="text-lampe">{name(duell.verliererMemberId)}</span> übernimmt.
        </p>
      )}

      <div className="grid grid-cols-2 gap-2">
        {posten.schnapsId === null && (
          <>
            <Amtsknopf
              variante="leder"
              disabled={laeuft}
              onClick={() =>
                void rufe<RouletteAntwort>('roulette').then((a) => a && setRoulette(a))
              }
            >
              Roulette
            </Amtsknopf>
            <Amtsknopf
              variante="leder"
              disabled={laeuft}
              onClick={() =>
                void rufe<ZufallsrichterAntwort>('zufallsrichter').then((a) => a && setRichter(a))
              }
            >
              Zufallsrichter
            </Amtsknopf>
          </>
        )}
        {!duell && (
          <Amtsknopf
            variante="leder"
            disabled={laeuft}
            onClick={() =>
              void rufe<DuellAntwort>('wuerfelduell').then((a) => {
                if (a) {
                  setDuell(a)
                  neuLaden()
                }
              })
            }
          >
            Würfelduell
          </Amtsknopf>
        )}
        {posten.modifier === 'joker' && posten.memberId === eigeneMemberId && (
          <Amtsknopf variante="leder" disabled={laeuft} onClick={() => setJokerOffen((o) => !o)}>
            Joker spielen
          </Amtsknopf>
        )}
      </div>

      {jokerOffen && (
        <div className="flex flex-col gap-2">
          <p className="protokoll">Wer übernimmt die Einheit?</p>
          <div className="flex flex-wrap gap-1.5">
            {anwesende
              .filter((t) => t.memberId !== eigeneMemberId)
              .map((t) => (
                <button
                  key={t.memberId}
                  type="button"
                  disabled={laeuft}
                  onClick={() =>
                    void rufe('joker', { aufMemberId: t.memberId }).then((a) => {
                      if (a !== null) neuLaden()
                    })
                  }
                  className="border border-eiche px-3 py-1.5 text-sm text-akte/80 transition-colors hover:border-messing"
                >
                  {t.avatarEmoji} {t.name}
                </button>
              ))}
          </div>
        </div>
      )}

      <Fehlermeldung text={fehler} />
    </div>
  )
}
